/**
 * Gamification primitives · see DESIGN_LANGUAGE_v2 §8, §9.
 */
import React from "react";
import { motion } from "framer-motion";
import { DUR, EASE, ROLE_ACCENT } from "../../lib/motion";

export function TierBadge({ role = "driver", tier = "Partner", subtitle }) {
  const c = ROLE_ACCENT[role];
  return (
    <motion.div
      data-testid={`gamify-tier-badge-${role}`}
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: DUR.reveal, ease: EASE.pop }}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "6px 12px", borderRadius: 999,
        background: `${c}1F`, border: `1px solid ${c}55`,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c, boxShadow: `0 0 6px ${c}` }}/>
      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: c, fontWeight: 800 }}>{tier}</span>
      {subtitle && <span style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600 }}>{subtitle}</span>}
    </motion.div>
  );
}

export function StreakFlame({ count = 0, unit = "wk", color = "#F59E0B" }) {
  return (
    <motion.span
      data-testid="gamify-streak-flame"
      animate={{ rotate: [0, -3, 3, 0], scale: [0.98, 1.02, 0.98] }}
      transition={{ duration: 1.4, repeat: Infinity }}
      style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill={color}>
        <path d="M12 2s3 4 3 8a3 3 0 01-6 0c0-1 .5-2 1-3-1 1-2 2-3 4a5 5 0 1010 0c0-3-2-6-5-9z"/>
      </svg>
      <span style={{ fontSize: 11, fontWeight: 800, color, fontFamily: "ui-monospace,Menlo,monospace" }}>{count} {unit}</span>
    </motion.span>
  );
}

export function TenureRing({ percent = 0, currentTier = "Partner", nextTier = "Contender", tripsToNext = 25, size = 180 }) {
  const strokeW = 10;
  const r = size / 2 - strokeW;
  const c = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(100, percent)) / 100 * c;
  return (
    <div data-testid="gamify-tenure-ring" style={{ position: "relative", width: size, height: size, margin: "0 auto" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={strokeW} stroke="rgba(255,255,255,0.08)" fill="none"/>
        <motion.circle
          cx={size/2} cy={size/2} r={r}
          strokeWidth={strokeW} stroke="#F59E0B"
          fill="none" strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - filled }}
          transition={{ duration: DUR.narrative, ease: EASE.hold }}
          style={{ filter: "drop-shadow(0 0 8px #F59E0B)" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center",
      }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#94A3B8", textTransform: "uppercase", fontWeight: 800 }}>{currentTier}</div>
        <div style={{ fontSize: 28, fontWeight: 900, color: "#E2E8F0" }}>{Math.round(percent)}%</div>
        <div style={{ fontSize: 10, color: "#F59E0B", fontWeight: 700, marginTop: 4 }}>{tripsToNext} to {nextTier}</div>
      </div>
    </div>
  );
}

export function DailyGoalCard({ label, done = 0, target = 5, unit = "rides" }) {
  const pct = target > 0 ? Math.min(100, (done / target) * 100) : 0;
  const complete = done >= target;
  return (
    <motion.div
      data-testid="gamify-daily-goal"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DUR.reveal, ease: EASE.emerge }}
      style={{
        padding: 14, borderRadius: 12,
        background: complete ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.05)",
        border: `1px solid ${complete ? "#10B98155" : "rgba(255,255,255,0.10)"}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800 }}>Today&apos;s Goal</div>
        <div style={{ fontSize: 12, color: complete ? "#10B981" : "#E2E8F0", fontWeight: 800 }}>{done}/{target} {unit}</div>
      </div>
      <div style={{ fontSize: 13, color: "#E2E8F0", marginBottom: 10 }}>{label}</div>
      <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 4, overflow: "hidden" }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: DUR.narrative, ease: EASE.hold }}
          style={{ height: "100%", background: complete ? "#10B981" : "#F59E0B" }}
        />
      </div>
    </motion.div>
  );
}
